import { visibleIntakeQuestions } from "@bloom/shared";
import type {
  IntakeAnswer,
  PublicIntakeProgram,
  IntakeProgramSubmission,
} from "@bloom/shared";
export function programErrors(
  program: PublicIntakeProgram,
  submission?: IntakeProgramSubmission,
): string[] {
  if (!program.active)
    return [program.name + " is unavailable. Contact your clinic."];
  if (!submission || submission.revision !== program.revision)
    return ["Complete the current assessment for " + program.name];
  if (program.dosagePlans?.length && !submission.path)
    return ["Choose starter or continuation"];
  if (
    program.dosagePlans?.length &&
    submission.entry === "direct" &&
    !submission.planId
  )
    return ["Choose your dosage plan"];
  return visibleIntakeQuestions(
    program,
    submission.answers,
    submission,
  ).flatMap((q) => {
    const value = submission.answers[q.id];
    if (value === undefined || value === "")
      return q.required ? [q.prompt] : [];
    if (
      q.type === "text" &&
      (typeof value !== "string" || (q.required && !value.trim()))
    )
      return [q.prompt];
    if (q.type === "boolean" && typeof value !== "boolean") return [q.prompt];
    if (
      q.type === "choice" &&
      (typeof value !== "string" || !q.choices?.includes(value))
    )
      return [q.prompt];
    if (
      q.type === "number" &&
      (typeof value !== "number" ||
        !Number.isFinite(value) ||
        (q.min !== undefined && value < q.min) ||
        (q.max !== undefined && value > q.max))
    )
      return [q.prompt];
    return [];
  });
}
export function ProgramQuestions({
  program,
  answers,
  onChange,
  context = {},
}: {
  context?: Pick<IntakeProgramSubmission, "path" | "planId">;
  program: PublicIntakeProgram;
  answers: Record<string, IntakeAnswer>;
  onChange: (answers: Record<string, IntakeAnswer>) => void;
}) {
  const change = (id: string, next: Record<string, IntakeAnswer>) => {
    // Clear transitive dependents whenever their prerequisite changes.
    const invalid = new Set([id]);
    for (const q of program.questions)
      if (q.when?.some((c) => invalid.has(c.questionId))) {
        delete next[q.id];
        invalid.add(q.id);
      }
    const visible = new Set(
      visibleIntakeQuestions(program, next, context).map((q) => q.id),
    );
    onChange(
      Object.fromEntries(
        Object.entries(next).filter(([id]) => visible.has(id)),
      ),
    );
  };
  return (
    <div className="space-y-5">
      {visibleIntakeQuestions(program, answers, context).map((q) => (
        <label key={q.id} className="block text-sm font-medium text-gray-800">
          {q.prompt}
          {q.required ? " *" : " (optional)"}
          {q.type === "text" ? (
            <textarea
              className="mt-2 w-full rounded-lg border p-3"
              maxLength={300}
              value={String(answers[q.id] || "")}
              onChange={(e) =>
                change(q.id, { ...answers, [q.id]: e.target.value })
              }
            />
          ) : q.type === "number" ? (
            <input
              type="number"
              min={q.min}
              max={q.max}
              step="any"
              value={answers[q.id] === undefined ? "" : String(answers[q.id])}
              className="mt-2 w-full rounded-lg border p-3"
              onChange={(e) => {
                const next = { ...answers };
                if (e.target.value === "") delete next[q.id];
                else next[q.id] = Number(e.target.value);
                change(q.id, next);
              }}
            />
          ) : (
            <select
              className="mt-2 w-full rounded-lg border p-3"
              value={answers[q.id] === undefined ? "" : String(answers[q.id])}
              onChange={(e) => {
                const next = { ...answers };
                if (e.target.value === "") delete next[q.id];
                else
                  next[q.id] =
                    q.type === "boolean"
                      ? e.target.value === "true"
                      : e.target.value;
                change(q.id, next);
              }}
            >
              <option value="">Choose an answer</option>
              {q.type === "boolean" ? (
                <>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </>
              ) : (
                q.choices?.map((c) => <option key={c}>{c}</option>)
              )}
            </select>
          )}
        </label>
      ))}
    </div>
  );
}
